import {
  CorporateWellnessContent,
  EventsContent,
  FundraisersContent,
} from 'src/environments/environment.interface';

/** Empty nested shells so templates never dereference undefined sections. */
export const EMPTY_CORPORATE_CONTENT: Required<CorporateWellnessContent> = {
  hero: {
    eyebrow: '',
    title: '',
    lede: '',
    image: '',
    ctaPrimary: { label: '', href: '' },
    ctaSecondary: { label: '', href: '' },
  },
  businessCase: {
    eyebrow: '',
    title: '',
    intro: '',
    calloutBold: '',
    calloutNote: '',
    stats: [],
  },
  program: { eyebrow: '', title: '', intro: '', phases: [] },
  banner: { title: '', text: '', ctaLabel: '' },
  contact: {
    eyebrow: '',
    title: '',
    intro: '',
    email: '',
    disclaimer: '',
    successNote: '',
    interestOptions: [],
  },
};

export const EMPTY_EVENTS_CONTENT: Required<EventsContent> = {
  hero: { eyebrow: '', title: '', kicker: '', image: '', ctaLabel: '' },
  labelBar: '',
  hook: '',
  perks: { circle: [], heading: '', includes: [], note: '' },
  gallery: { heading: '', images: [] },
  cta: { heading: '', text: '', ctaLabel: '', inquiryUrl: '', email: '' },
};

export const EMPTY_FUNDRAISERS_CONTENT: Required<FundraisersContent> = {
  hero: {
    eyebrow: '',
    title: '',
    tagline: '',
    lede: '',
    image: '',
    ctaPrimaryLabel: '',
  },
  oneSheetUrl: '',
  googleFormUrl: '',
  email: '',
  howItWorks: { heading: '', intro: '', includesHeading: '', includes: [] },
  whyUs: { heading: '', intro: '', stats: [] },
  decade: { heading: '', intro: '' },
  photos: [],
  oneSheetCallout: { heading: '', text: '' },
  form: { heading: '', intro: '', successNote: '' },
};

/** Merge tenant content onto empty shells (section by section). */
export function mergeCorporateContent(
  content?: CorporateWellnessContent
): Required<CorporateWellnessContent> {
  const c = content || {};
  const d = EMPTY_CORPORATE_CONTENT;
  return {
    hero: {
      ...d.hero,
      ...c.hero,
      ctaPrimary: { ...d.hero.ctaPrimary, ...c.hero?.ctaPrimary },
      ctaSecondary: { ...d.hero.ctaSecondary, ...c.hero?.ctaSecondary },
    },
    businessCase: {
      ...d.businessCase,
      ...c.businessCase,
      stats: c.businessCase?.stats ?? d.businessCase.stats,
    },
    program: {
      ...d.program,
      ...c.program,
      phases: c.program?.phases ?? d.program.phases,
    },
    banner: { ...d.banner, ...c.banner },
    contact: {
      ...d.contact,
      ...c.contact,
      interestOptions: c.contact?.interestOptions ?? d.contact.interestOptions,
    },
  };
}

export function mergeEventsContent(content?: EventsContent): Required<EventsContent> {
  const c = content || {};
  const d = EMPTY_EVENTS_CONTENT;
  return {
    hero: { ...d.hero, ...c.hero },
    labelBar: c.labelBar ?? d.labelBar,
    hook: c.hook ?? d.hook,
    perks: {
      ...d.perks,
      ...c.perks,
      circle: c.perks?.circle ?? d.perks.circle,
      includes: c.perks?.includes ?? d.perks.includes,
    },
    gallery: {
      ...d.gallery,
      ...c.gallery,
      images: c.gallery?.images ?? d.gallery.images,
    },
    cta: { ...d.cta, ...c.cta },
  };
}

export function mergeFundraisersContent(
  content?: FundraisersContent
): Required<FundraisersContent> {
  const c = content || {};
  const d = EMPTY_FUNDRAISERS_CONTENT;
  return {
    hero: { ...d.hero, ...c.hero },
    oneSheetUrl: c.oneSheetUrl ?? d.oneSheetUrl,
    googleFormUrl: c.googleFormUrl ?? d.googleFormUrl,
    email: c.email ?? d.email,
    howItWorks: {
      ...d.howItWorks,
      ...c.howItWorks,
      includes: c.howItWorks?.includes ?? d.howItWorks.includes,
    },
    whyUs: {
      ...d.whyUs,
      ...c.whyUs,
      stats: c.whyUs?.stats ?? d.whyUs.stats,
    },
    decade: { ...d.decade, ...c.decade },
    photos: c.photos?.length ? c.photos : d.photos,
    oneSheetCallout: { ...d.oneSheetCallout, ...c.oneSheetCallout },
    form: { ...d.form, ...c.form },
  };
}

/** Update the real `<link rel="canonical">` (not a meta name=canonical tag). */
export function setCanonicalHref(url: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const link: HTMLLinkElement =
    document.querySelector("link[rel='canonical']") || document.createElement('link');
  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', url);
  if (!link.parentNode) {
    document.head.appendChild(link);
  }
}
